/* eslint-disable no-magic-numbers */
import React, { useState } from 'react';
import classNames from 'classnames';
import clsx from 'clsx';
import { format } from 'date-fns';
import get from 'lodash/get';
import { useParams } from 'react-router-dom';

import Icon from 'carbon-react/lib/components/icon';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { CustomPopover, toastError } from '@futrli/ui-components';
import { FormattedMessage } from '@futrli/localisation';
import { Logger, REQUEST_METHODS } from '@futrli/shared-services';

import {
  approveDispatchedReport,
  deleteDispatchedReport,
  denyDispatchedReport,
  getScheduleReportDetails
} from '../../../core/api/reporting/report-scheduling';
import { Data } from '../../../utils/table-filters/filter-table-types';
import { DeleteScheduleReportDialog } from './delete-schedule-report-dialog';
import { downloadFile, viewFile } from './utils';
import { getHoursMinutes } from '../../../utils/dates';
import { makeRequestWithResponse } from '../../../core/api/shared';
import { NormalizedReportScheduleItem } from '../../../report-builder/components/document-library/scheduled-reports/scheduled-reports';
import { ScheduleReportDialog } from '../../../report-builder/components/schedule-report-dialog/schedule-report-dialog';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';

import * as styles from '../virtualized-list.scss';

interface Props {
  rowData?: Data;
  rowIndex: number;
  revalidateData?: () => Promise<void>;
}

export const AwaitingReviewTableRow = ({ rowData, rowIndex, revalidateData }: Props) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isOpenScheduleReportDialog, setIsOpenScheduleReportDialog] = useState(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scheduledReportDetails, setScheduleReportDetails] = useState<NormalizedReportScheduleItem>();
  const [isViewing, setIsViewing] = useState(false);
  const [popoverAnchorElement, setPopoverAnchorElement] = useState<HTMLElement | null>(null);

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { isConsolidation } = usePredictionsInfoContext();

  const onDownloadClick = async () => {
    try {
      setIsDownloading(true);
      const downloadLink = get(rowData, 'download_link', '');
      if (downloadLink && rowData?.name) {
        const downloadURL: { url: string } = await makeRequestWithResponse({
          url: downloadLink,
          method: REQUEST_METHODS.GET
        });
        await downloadFile(downloadURL.url, `${rowData.name}.pdf`);
      }
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsDownloading(false);
    }
  };

  const onViewClick = async () => {
    try {
      setIsViewing(true);
      const downloadLink = get(rowData, 'download_link', '');
      if (downloadLink && rowData?.name) {
        const downloadURL: { view_url: string } = await makeRequestWithResponse({
          url: downloadLink,
          method: REQUEST_METHODS.GET
        });
        await viewFile(downloadURL.view_url);
      }
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsViewing(false);
    }
  };

  const handleOpenScheduleDialog = () => {
    setIsOpenScheduleReportDialog(true);
  };

  const handleCloseScheduleDialog = () => {
    setIsOpenScheduleReportDialog(false);
  };

  const handleOpenDeleteDialog = () => {
    setIsOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsOpenDeleteDialog(false);
  };

  const handleApproveClick = async () => {
    if (rowData?.id) {
      try {
        await approveDispatchedReport(workspaceId, organisationId, rowData?.id, isConsolidation);
        revalidateData?.();
      } catch (e) {
        Logger.error(e as string);
      }
    }
  };

  const handleDenyClick = async () => {
    if (rowData?.id) {
      try {
        await denyDispatchedReport(workspaceId, organisationId, rowData?.id, isConsolidation);
        revalidateData?.();
      } catch (e) {
        Logger.error(e as string);
      }
    }
  };

  const handleDelete = async () => {
    if (rowData?.id) {
      try {
        setIsLoading(true);
        await deleteDispatchedReport(workspaceId, organisationId, rowData.id, isConsolidation);
        revalidateData?.();
        handleCloseDeleteDialog();
      } catch (e) {
        Logger.error(e as string);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleEditClick = async () => {
    const scheduleReportId = get(rowData, 'report_schedule_id', '');
    if (scheduleReportId) {
      try {
        setIsLoading(true);
        handleOpenScheduleDialog();
        const fetchedScheduleReportDetails = await getScheduleReportDetails(
          workspaceId,
          organisationId,
          scheduleReportId,
          isConsolidation
        );
        setScheduleReportDetails({
          ...fetchedScheduleReportDetails,
          // TODO: what to use when  next_generate_at is null
          next_generate_at: fetchedScheduleReportDetails.next_generate_at
            ? new Date(fetchedScheduleReportDetails.next_generate_at)
            : fetchedScheduleReportDetails.end_date
            ? new Date(fetchedScheduleReportDetails.end_date)
            : new Date(fetchedScheduleReportDetails.start_date),
          users: fetchedScheduleReportDetails.users
            .map(u => u.email_address)
            .join(', ')
            .trim(),
          time: fetchedScheduleReportDetails.next_generate_at
            ? getHoursMinutes(fetchedScheduleReportDetails.next_generate_at)
            : fetchedScheduleReportDetails.end_date
            ? getHoursMinutes(fetchedScheduleReportDetails.end_date)
            : getHoursMinutes(fetchedScheduleReportDetails.start_date)
        });
      } catch (e) {
        Logger.error(e as string);
        toastError();
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handlePopoverClose = () => {
    setPopoverAnchorElement(null);
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchorElement(event.currentTarget);
  };

  if (!rowData) {
    return null;
  }

  return (
    <>
      <div
        role="row"
        key={rowData.id}
        className={clsx(styles.virtualizedListRow)}
        style={{ backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : 'transparent', position: 'relative' }}
      >
        <div
          role="cell"
          className={clsx(
            styles.virtualizedListCell,
            styles.flex2,
            styles.documentLibraryTableFont,
            styles.svgContainer
          )}
          style={{ minWidth: 0, border: 'none' }}
        >
          <Icon type="file_pdf" />
          <span className={styles.textOverflow}>{`${rowData.name}.pdf`}</span>
        </div>
        {rowData.next_generate_at ? (
          <div
            role="cell"
            className={clsx(styles.virtualizedListCell, styles.flex1, styles.documentLibraryTableFont)}
            style={{ minWidth: 0, border: 'none' }}
          >
            <span className={styles.textOverflow}>{format(rowData.next_generate_at, 'dd/MM/yyyy')}</span>
          </div>
        ) : null}
        {rowData.time ? (
          <div
            role="cell"
            className={clsx(styles.virtualizedListCell, styles.flex1, styles.documentLibraryTableFont)}
            style={{ minWidth: 0, border: 'none' }}
          >
            <span className={styles.textOverflow}>{rowData.time}</span>
          </div>
        ) : null}
        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex2, styles.documentLibraryTableFont)}
          style={{ minWidth: 0, border: 'none' }}
        >
          <span>{rowData.period}</span>
        </div>
        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex2, styles.documentLibraryTableFont)}
          style={{ minWidth: 0, border: 'none' }}
        >
          <button onClick={handlePopoverOpen} className={styles.popoverBtn}>
            <FormattedMessage id="predict.options_send_not_send" defaultMessage="Options (send/ do not send)" />
            <Icon type="caret_down" />
          </button>
          <CustomPopover
            open={Boolean(popoverAnchorElement)}
            anchorEl={popoverAnchorElement}
            onClose={handlePopoverClose}
            id="custom-date-select-popover"
            offset={{ x: 0, y: 5 }}
            style={{ width: 280 }}
          >
            <button onClick={handleApproveClick} className={styles.menuBtn}>
              <FormattedMessage id="predict.send_move_to_sent" defaultMessage="Send (and move to sent)" />
            </button>
            <button onClick={handleDenyClick} className={styles.menuBtn}>
              <FormattedMessage
                id="predict.do_not_send_move_exported"
                defaultMessage="Do not send (and move to exported)"
              />
            </button>
          </CustomPopover>
        </div>
        <Tooltip message={rowData.users}>
          <div
            role="cell"
            className={clsx(styles.virtualizedListCell, styles.flex4, styles.documentLibraryTableFont)}
            style={{ minWidth: 0, border: 'none' }}
          >
            <div
              style={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                width: 'calc(100% - 125px)'
              }}
            >
              {rowData.users}
            </div>
          </div>
        </Tooltip>
        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex1, styles.documentLibraryTableFont)}
          style={{
            position: 'absolute',
            right: 0,
            display: 'flex',
            backgroundColor: '#f6f7fa',
            justifyContent: 'center',
            paddingRight: 0
          }}
        >
          <div className={styles.iconContainer} onClick={handleEditClick} style={{ marginRight: 12 }}>
            <Icon type="edit" />
          </div>
          <div className={styles.iconContainer} onClick={handleOpenDeleteDialog} style={{ marginRight: 12 }}>
            <Icon type="bin" />
          </div>
          <div
            className={classNames(styles.iconContainer, {
              [styles.disabledIcon]: isViewing
            })}
            onClick={onViewClick}
            style={{ marginRight: 12 }}
          >
            <Icon type="view" />
          </div>
          <div
            className={classNames(styles.iconContainer, {
              [styles.disabledIcon]: isDownloading
            })}
            onClick={onDownloadClick}
          >
            <Icon type="download" />
          </div>
        </div>
      </div>

      {isOpenScheduleReportDialog && (
        <ScheduleReportDialog
          isOpen={isOpenScheduleReportDialog}
          handleClose={handleCloseScheduleDialog}
          reportName={rowData.pdf_name}
          reportId={get(scheduledReportDetails, 'report', '')}
          initialData={scheduledReportDetails}
          revalidateData={revalidateData}
          isLoading={isLoading}
        />
      )}

      {isOpenDeleteDialog && (
        <DeleteScheduleReportDialog
          isOpen={isOpenDeleteDialog}
          handleCloseDialog={handleCloseDeleteDialog}
          onSubmit={handleDelete}
          isLoading={isLoading}
        />
      )}
    </>
  );
};
